import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { formatRoleRef, parseRoleRef, type CurrentUser, type UserPermissions } from '@arpm/shared';

import { apiClient, ApiClientError } from '@/api/client.js';
import { completeLogin, getStoredUser, startLogin, startLogout, userManager } from '@/auth/oidc.js';

/**
 * Декодирует payload JWT без проверки подписи.
 * Подпись проверяет backend через JWKS — фронту нужны только claims
 * (роли, имя), и для UI этого достаточно.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1]!;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    // atob → байты → utf-8 строка
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Хранит информацию о текущем пользователе и токенах.
 *
 * Синхронизируется с UserManager (oidc-client-ts):
 *   - При initialize() читаем сохранённого пользователя
 *   - Подписываемся на события UserManager (silent renew, expired)
 */
export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null);
  const user = ref<CurrentUser | null>(null);
  /**
   * Серверные разрешения текущего пользователя (из /v1/me/permissions).
   * Загружаются после JWT-гидрации. До завершения загрузки `isAdmin` = false.
   */
  const permissions = ref<UserPermissions | null>(null);
  const isLoading = ref(true);

  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);

  /** Серверный флаг — есть ли у пользователя глобальная роль lk-admin. */
  const isAdmin = computed(() => permissions.value?.isAdmin ?? false);

  /** Плоский набор ролей в строковой форме `lk-admin` / `<svc>:<code>`. */
  const roleStrings = computed<string[]>(() =>
    permissions.value?.roles.map(formatRoleRef) ?? [],
  );

  /**
   * Подтверждён ли email — из серверных permissions (источник истины — БД ЛК),
   * НЕ из claim токена (он под моделью B всегда true). Пока permissions не
   * загружены — считаем подтверждённым, чтобы не мигал баннер.
   */
  const emailVerified = computed<boolean>(() => permissions.value?.emailVerified ?? true);

  function flattenRoles(profile: { resource_access?: Record<string, { roles: string[] }> }): string[] {
    const result: string[] = [];
    const access = profile.resource_access ?? {};
    for (const [client, { roles }] of Object.entries(access)) {
      for (const role of roles) {
        result.push(`${client}:${role}`);
      }
    }
    return result;
  }

  function hydrateFromOidcUser(oidcUser: import('oidc-client-ts').User | null): void {
    if (!oidcUser || oidcUser.expired) {
      accessToken.value = null;
      user.value = null;
      return;
    }
    accessToken.value = oidcUser.access_token;

    // Роли и группы есть только в access_token (resource_access),
    // а не в id_token (oidcUser.profile). Поэтому декодируем access_token.
    const accessPayload = decodeJwtPayload(oidcUser.access_token) ?? {};
    const roles = flattenRoles(
      accessPayload as { resource_access?: Record<string, { roles: string[] }> },
    );
    const groups = (accessPayload.groups as string[] | undefined) ?? [];

    // Базовые поля профиля одинаковы в id_token и access_token.
    // Берём из id_token (oidcUser.profile) — это типизированный объект.
    const profile = oidcUser.profile as Record<string, unknown>;
    const firstName = (profile.given_name as string | undefined) ?? '';
    const lastName = (profile.family_name as string | undefined) ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const email = (profile.email as string | undefined) ?? '';

    user.value = {
      id: oidcUser.profile.sub,
      email,
      emailVerified: !!profile.email_verified,
      firstName,
      lastName,
      fullName,
      displayName: fullName || email,
      username: (profile.preferred_username as string | undefined) ?? email,
      roles,
      groups,
      avatarUrl: profile.picture as string | undefined,
      // Эти два поля наполнятся серверным ответом /v1/me/permissions —
      // здесь только инициализируем дефолтами.
      isAdmin: false,
      managedGroups: [],
    };
  }

  /**
   * Подтянуть актуальные разрешения с бэка. Вызывается после смены токена
   * или вручную (при подозрении на устаревший кэш).
   */
  async function loadPermissions(): Promise<void> {
    if (!accessToken.value) {
      permissions.value = null;
      return;
    }
    try {
      const data = await apiClient.get<UserPermissions>('/api/v1/me/permissions');
      permissions.value = data;
    } catch (err) {
      if (err instanceof ApiClientError && err.statusCode === 401) {
        permissions.value = null;
        return;
      }
      // eslint-disable-next-line no-console
      console.error('Не удалось загрузить /me/permissions', err);
      permissions.value = null;
    }
  }

  async function initialize(): Promise<void> {
    isLoading.value = true;
    try {
      const oidcUser = await getStoredUser();
      hydrateFromOidcUser(oidcUser);
      if (accessToken.value) {
        await loadPermissions();
      }

      // Подписки на обновления токенов
      userManager.events.addUserLoaded(async (u) => {
        hydrateFromOidcUser(u);
        if (accessToken.value) {
          await loadPermissions();
        }
      });
      userManager.events.addUserUnloaded(() => {
        hydrateFromOidcUser(null);
        permissions.value = null;
      });
      userManager.events.addAccessTokenExpired(() => {
        hydrateFromOidcUser(null);
        permissions.value = null;
      });
    } finally {
      isLoading.value = false;
    }
  }

  async function login(returnTo?: string): Promise<void> {
    await startLogin(returnTo);
  }

  async function handleCallback(): Promise<string> {
    const { user: oidcUser, returnTo } = await completeLogin();
    hydrateFromOidcUser(oidcUser);
    if (accessToken.value) {
      await loadPermissions();
    }
    return returnTo;
  }

  async function logout(): Promise<void> {
    await startLogout();
  }

  /**
   * Проверка роли в новой модели (см. ADR-018).
   *
   * Принимает строку:
   *   - `lk-admin` (глобальная роль ЛК)
   *   - `<serviceCode>:<code>` (роль сервиса)
   *
   * Старый Keycloak-формат `lk-arpm:admin` не поддерживается — фронт
   * больше не работает с ним.
   */
  function hasRole(role: string): boolean {
    const target = parseRoleRef(role);
    return (
      permissions.value?.roles.some(
        (r) => r.serviceCode === target.serviceCode && r.code === target.code,
      ) ?? false
    );
  }

  return {
    accessToken,
    user,
    permissions,
    isAuthenticated,
    isAdmin,
    roleStrings,
    emailVerified,
    isLoading,
    initialize,
    login,
    handleCallback,
    logout,
    loadPermissions,
    hasRole,
  };
});
